import React, { useEffect, useState } from 'react';
import { FiAlertTriangle, FiCheckCircle, FiCpu, FiInfo } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { useNavRefresh } from '../../context/NavContext';
import API from '../../utils/api';
import styles from './AdminDashboard.module.css';

// Skeleton loaders for different content types
function SkeletonCard() {
  return <div className={styles.skeletonCard} />;
}

function SkeletonChart() {
  return (
    <div className={styles.chartContainer}>
      <div className={styles.skeletonText} style={{ width: '150px', marginBottom: '1rem' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
        {[1, 2, 3].map(i => (
          <div key={i} className={styles.skeletonBar} />
        ))}
      </div>
    </div>
  );
}

function SkeletonMetricCard() {
  return (
    <div className={styles.skeletonMetricCard}>
      <div className={styles.skeletonText} style={{ width: '80px', height: '28px', marginBottom: '0.8rem' }} />
      <div className={styles.skeletonText} style={{ width: '120px', height: '16px', marginBottom: '0.6rem' }} />
      <div className={styles.skeletonText} style={{ width: '100%', height: '14px' }} />
    </div>
  );
}

// Simple chart component for score distribution
function ScoreDistributionChart({ distribution }) {
  if (!distribution) return null;
  
  const total = (distribution?.A || 0) + (distribution?.B || 0) + (distribution?.C || 0) + (distribution?.D || 0) + (distribution?.F || 0);
  
  if (total === 0) {
    return (
      <div className={styles.chartContainer}>
        <div className={styles.chartLabel}>Score Distribution</div>
        <div className={styles.emptyChart}>No assessment data yet</div>
      </div>
    );
  }
  
  // Grade scale with colors matching grading system
  const grades = [
    { grade: 'A', range: '80-100%', count: distribution.A, color: '#1a5c38' },
    { grade: 'B', range: '70-79%', count: distribution.B, color: '#27ae60' },
    { grade: 'C', range: '60-69%', count: distribution.C, color: '#e67e22' },
    { grade: 'D', range: '50-59%', count: distribution.D, color: '#d35400' },
    { grade: 'F', range: '0-49%', count: distribution.F, color: '#c0392b' }
  ];

  return (
    <div className={styles.chartContainer}>
      <div className={styles.chartLabel}>Score Distribution</div>
      <div className={styles.barChart}>
        {grades.map(g => {
          const pct = Math.round((g.count / total) * 100);
          return (
            <div key={g.grade} className={styles.barGroup}>
              <div className={styles.barLabel}>Grade {g.grade} ({g.range})</div>
              <div className={styles.barTrack}>
                <div className={styles.bar} style={{ width: `${pct || 5}%`, backgroundColor: g.color }} />
              </div>
              <div className={styles.barValue}>{g.count}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Role breakdown chart
function RoleBreakdownChart({ byRole }) {
  if (!byRole) return null;

  const total = (byRole?.RSM || 0) + (byRole?.FSM || 0) + (byRole?.TSE || 0);
  
  if (total === 0) {
    return (
      <div className={styles.chartContainer}>
        <div className={styles.chartLabel}>Candidates by Role</div>
        <div className={styles.emptyChart}>No assessment data yet</div>
      </div>
    );
  }

  const roles = [
    { key: 'RSM', label: 'Regional Sales Mgr', color: '#1a5c38' },
    { key: 'FSM', label: 'Field Sales Mgr', color: '#27ae60' },
    { key: 'TSE', label: 'Territory Sales Mgr', color: '#e67e22' }
  ];

  return (
    <div className={styles.chartContainer}>
      <div className={styles.chartLabel}>Candidates by Role</div>
      <div className={styles.roleChart}>
        {roles.map(r => {
          const count = byRole?.[r.key] || 0;
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          return (
            <div key={r.key} className={styles.roleItem}>
              <div className={styles.roleHeader}>
                <span className={styles.roleLabel}>{r.label}</span>
                <span className={styles.roleCount}>{count}</span>
              </div>
              <div className={styles.roleBar}>
                <div className={styles.roleFill} style={{ width: `${Math.max(pct, 5)}%`, backgroundColor: r.color }} />
              </div>
              <div className={styles.rolePct}>{pct}%</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [aiInsightsLoading, setAiInsightsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState('all');
  const { refreshKey } = useNavRefresh();
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const statsRes = await API.get(`/admin/stats?period=${timePeriod}`);
        setStats(statsRes.data);
        
        // If aiInsights are in the response, use them immediately
        if (statsRes.data.aiInsights) {
          setAiInsights(statsRes.data.aiInsights);
        } else {
          // If not available yet, poll for them after a short delay
          setAiInsightsLoading(true);
          pollForAiInsights(timePeriod);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [refreshKey, timePeriod]);

  // Poll for AI insights every 2 seconds for up to 10 seconds
  const pollForAiInsights = async (period) => {
    let attempts = 0;
    const maxAttempts = 5;
    
    const poll = async () => {
      try {
        const res = await API.get(`/admin/stats?period=${period}`);
        if (res.data.aiInsights) {
          setAiInsights(res.data.aiInsights);
          setAiInsightsLoading(false);
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          setAiInsightsLoading(false);
        }
      } catch (e) {
        console.error('Poll error:', e);
        setAiInsightsLoading(false);
      }
    };
    
    poll();
  };

  const getRoleFull = r => ({ RSM: 'Regional Sales Manager', FSM: 'Field Sales Manager', TSE: 'Territory Sales Manager' }[r] || r);
  const getGradeColor = g => ({ A: '#1a5c38', B: '#27ae60', C: '#e67e22', D: '#e67e22', F: '#c0392b' }[g] || '#6b6b6b');

  // Skeleton loading state
  if (loading) {
    return (
      <AdminLayout>
        <div className={styles.page}>
          <div className={styles.pageHeader}>
            <div>
              <h2 className={styles.pageTitle}>Dashboard</h2>
              <p className={styles.pageSub}>Assessment overview and activity summary</p>
            </div>
          </div>

          {/* Skeleton for AI Summary */}
          <div className={styles.skeletonAiSummary} />

          {/* Skeleton for Stats */}
          <div className={styles.statsGrid}>
            {[1, 2, 3, 4, 5].map(i => <SkeletonCard key={i} />)}
          </div>

          {/* Skeleton for Key Metrics */}
          <div className={styles.section}>
            <div className={styles.skeletonText} style={{ width: '200px', height: '20px', marginBottom: '1.5rem' }} />
            <div className={styles.metricsGrid}>
              {[1, 2, 3].map(i => <SkeletonMetricCard key={i} />)}
            </div>
          </div>

          {/* Skeleton for Charts */}
          <div className={styles.section}>
            <div className={styles.skeletonText} style={{ width: '200px', height: '20px', marginBottom: '1.5rem' }} />
            <div className={styles.chartsGrid}>
              <SkeletonChart />
              <SkeletonChart />
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Use state-managed aiInsights or fallback to stats response (for backward compatibility)
  const displayInsights = aiInsights || stats?.aiInsights;

  return (
    <AdminLayout>
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <div>
            <h2 className={styles.pageTitle}>Dashboard</h2>
            <p className={styles.pageSub}>Assessment overview and activity summary</p>
          </div>
          <div className={styles.headerActions}>
            <select 
              value={timePeriod} 
              onChange={e => setTimePeriod(e.target.value)}
              className={styles.timePeriodSelect}
            >
              <option value="all">All Time</option>
              <option value="daily">Daily Report</option>
              <option value="weekly">Weekly Report</option>
              <option value="quarterly">Quarterly Report</option>
              <option value="yearly">Yearly Report</option>
            </select>
            <button className={styles.actionBtn} onClick={() => navigate('/admin/candidates')}>
              + New Candidate
            </button>
          </div>
        </div>

        {/* AI Executive Summary */}
        {displayInsights?.executiveSummary && (
          <div className={styles.aiSummary}>
            <div className={styles.aiIcon}><FiCpu aria-hidden="true" /></div>
            <div>
              <div className={styles.aiLabel}>AI Insights</div>
              <div className={styles.aiText}>{displayInsights.executiveSummary}</div>
            </div>
          </div>
        )}

        {/* AI Loading State */}
        {aiInsightsLoading && !displayInsights?.executiveSummary && (
          <div className={styles.aiSummary}>
            <div className={styles.aiIcon}><FiCpu aria-hidden="true" /></div>
            <div>
              <div className={styles.aiLabel}>AI Insights</div>
              <div className={styles.aiLoadingText}>
                <span className={styles.loadingSpinner}></span>
                Loading AI Insight...
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className={styles.statsGrid}>
          {[
            { label: 'Total Candidates', value: stats?.total || 0, sub: 'Registered' },
            { label: 'Pending', value: stats?.pending || 0, sub: 'Not started' },
            { label: 'In Progress', value: stats?.inProgress || 0, sub: 'Currently active' },
            { label: 'Completed', value: stats?.completed || 0, sub: 'Submitted' },
            { label: 'Advanced', value: stats?.advanced || 0, sub: 'Recommended' },
          ].map(s => (
            <div key={s.label} className={styles.statCard}>
              <span className={styles.statValue}>{s.value}</span>
              <span className={styles.statLabel}>{s.label}</span>
              <span className={styles.statSub}>{s.sub}</span>
            </div>
          ))}
        </div>

        {/* Key Metrics from AI */}
        {displayInsights?.keyMetrics && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Key Performance Metrics</h3>
            </div>
            <div className={styles.metricsGrid}>
              {displayInsights.keyMetrics.map((m, i) => (
                <div key={i} className={styles.metricCard}>
                  <div className={styles.metricValue}>{m.value}</div>
                  <div className={styles.metricLabel}>{m.metric}</div>
                  <div className={styles.metricInsight}>{m.insight}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Charts */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Assessment Analytics</h3>
          </div>
          <div className={styles.chartsGrid}>
            <ScoreDistributionChart distribution={stats?.scoreDistribution} />
            <RoleBreakdownChart byRole={stats?.byRole} />
          </div>
        </div>

        {/* Alerts */}
        {displayInsights?.alerts && displayInsights.alerts.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Status Alerts</h3>
            </div>
            <div className={styles.alertsList}>
              {displayInsights.alerts.map((alert, i) => (
                <div key={i} className={`${styles.alert} ${styles[`alert${alert.type}`]}`}>
                  <span className={styles.alertIcon}>
                    {alert.type === 'positive'
                      ? <FiCheckCircle aria-hidden="true" />
                      : alert.type === 'warning'
                        ? <FiAlertTriangle aria-hidden="true" />
                        : <FiInfo aria-hidden="true" />}
                  </span>
                  <span className={styles.alertMessage}>{alert.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Role Insights */}
        {displayInsights?.roleInsights && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Role-Based Insights</h3>
            </div>
            <div className={styles.insightBox}>
              <p>{displayInsights.roleInsights}</p>
            </div>
          </div>
        )}

        {/* AI Recommendations */}
        {displayInsights?.recommendations && displayInsights.recommendations.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Strategic Recommendations</h3>
            </div>
            <div className={styles.recommendationsList}>
              {displayInsights.recommendations.map((rec, i) => (
                <div key={i} className={styles.recommendationItem}>
                  <span className={styles.recNum}>{i + 1}</span>
                  <span className={styles.recText}>{rec}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
